import type { ApiIssue, MessageKey, MessagePayloads } from '@ralph/contracts'
import { ApiRequestError } from '@/api/client.ts'
import type { Messages } from '@/i18n/messages/index.ts'

/**
 * Any failure this client can hold, as a sentence in the reader's language.
 *
 * ── The order, and why each step is where it is ──
 *
 *  1. A 401 short-circuits. Whatever the server said about it, the only thing a person can do
 *     is sign in again, and saying anything else wastes the one sentence they will read.
 *  2. A known `messageKey` wins. This is the point of the whole seam: the server named the
 *     sentence, the catalogue renders it, and the specifics it carries — which base, which
 *     limit, which agents — survive translation intact (PRD-27 D-4).
 *  3. An UNKNOWN key falls back to the server's English `message`. This is not a defect, it is
 *     the deploy-order guarantee: a server that has learned a key a client has not must
 *     degrade to prose rather than to a blank or a raw identifier. It is also why `message`
 *     is never removed from the wire.
 *  4. No structured issue at all — a proxy's HTML error page, a truncated body — and it
 *     becomes the generic sentence naming the status. The status number is kept because it is
 *     the one thing a person can quote to whoever runs the deployment.
 *
 * `response.statusText` is never used as prose anywhere in this path: it is a protocol token
 * ("Bad Gateway"), it is English by definition, and on HTTP/2 it is empty.
 */
export function describeApiError(t: Messages, error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) return t.common.sessionExpired
    const issue = error.issues[0]
    if (issue) return describeIssue(t, issue) ?? issue.message ?? t.common.requestFailed(error.status)
    return t.common.requestFailed(error.status)
  }
  // A DOMException, an abort, a `JSON.parse` failure: already in the browser's own language,
  // so it is passed through rather than wrapped in a translation of something else.
  if (error instanceof Error && error.message) return error.message
  return t.common.genericError
}

/**
 * One issue, if this build knows its key. `null` means "not mine to render" so the caller can
 * decide what to fall back to, rather than this returning a plausible-looking wrong sentence.
 */
export function describeIssue(t: Messages, issue: Pick<ApiIssue, 'messageKey' | 'params'>): string | null {
  const key = issue.messageKey
  if (!key || !(key in t.errors)) return null
  const render = t.errors[key as MessageKey] as (params: MessagePayloads[MessageKey]) => string
  try {
    return render((issue.params ?? {}) as MessagePayloads[MessageKey])
  } catch {
    // A key whose payload arrived malformed — a server one version ahead, a hand-rolled
    // client. Better the English sentence than a blank space where the refusal was.
    return null
  }
}

/**
 * Does this error carry a particular sentence? For the handful of places that branch on WHICH
 * refusal happened rather than merely showing it — an upload that offers "choose a smaller
 * file" only when the file was too large.
 *
 * It exists so that branching is done on the KEY and never on the prose. Matching prose to
 * choose prose is the pattern this PRD removed from three places in this codebase (D-6), and
 * it fails silently the moment either side is translated.
 */
export function issueIs(error: unknown, key: MessageKey): boolean {
  return error instanceof ApiRequestError && error.issues.some((issue) => issue.messageKey === key)
}
